import { OfferProgressionType } from "./../../../../../node_modules/@playcharge/common/models/backend/offers/offer-progression-type";
import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Signal,
    WritableSignal,
    effect,
    inject,
    signal,
} from "@angular/core";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { MatExpansionModule } from "@angular/material/expansion";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { MatOption, MatSelect } from "@angular/material/select";
import { BillingType } from "@playcharge/common/models/backend/offers/billing-type";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { OfferType } from "@playcharge/common/models/backend/offers/offer-type";
import { Platform } from "@playcharge/common/models/backend/auth/platform";
import { OfferPlacement } from "@playcharge/common/models/backend/offers/offer-placement";
import { MatCheckbox } from "@angular/material/checkbox";
import { MMPProviderType } from "@playcharge/common/models/backend/offers/mmpp-provider-type";
import {
    OfferAbTestsForm,
    OfferAchievementForm,
    OfferAnalyticsForm,
    OfferAppInfoForm,
    OfferBillingForm,
    OfferDurationForm,
    OfferGeneralForm,
    OfferMiscForm,
    OfferTargetingForm,
} from "../../../models/offers/offer-forms";
import { CampaignOfferGoalType } from "@playcharge/common/models/backend/offers/campaign-offer-goal-type";
import { EnumUtils } from "../../../utils/enum-utils";
import { DateTime } from "luxon";
import { GameCategoriesStore } from "../../../stores/offers/game-categories.store";
import { TargetGroupsStore } from "../../../stores/target-groups/target-groups.store";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { UpdateOfferEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-edit-model";
import { ToastrService } from "ngx-toastr";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { OfferStore } from "../../../stores/offers/offer.store";
import { environment } from "../../../../environments/environment";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AchievementsStore } from "../../../stores/achievements/achievements-store";
import { AchievementType } from "@playcharge/common/models/backend/achievements/achievement-type";
import { AchievementBackofficeViewModel } from "@playcharge/common/models/backend/achievements/backoffice/achievement-backoffice-view-model";
import { AbTestsStore } from "../../../stores/ab-tests/ab-tests.store";
import { AbTestViewModel } from "@playcharge/common/models/backend/ab-tests/ab-test-view-model";
import { OfferGenresStore } from "../../../stores/offers/offer-genres.store";
import { OfferGenreViewModel } from "@playcharge/common/models/backend/offers/offer-genre-view-model";
import { map } from "rxjs";
import { OfferSourceType } from "@playcharge/common/models/backend/offers/offer-source-type";
import { TippyDirective } from "@ngneat/helipopper";
import { OfferAdvertiserUrlParamsTooltipComponent } from "../offer-advertiser-url-params-tooltip/offer-advertiser-url-params-tooltip.component";
import { patchState, signalState } from "@ngrx/signals";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-offer",
    standalone: true,
    imports: [
        CommonModule,
        MatExpansionModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButton,
        MatSelect,
        MatOption,
        EnumKeyValuePipe,
        MatCheckbox,
        MatChipsModule,
        MatIcon,
        MatDatepickerModule,
        TippyDirective,
        OfferAdvertiserUrlParamsTooltipComponent,
        NoScrollDirective,
    ],
    templateUrl: "./offer.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OfferStore],
})
export class OfferComponent {
    private _toastrService = inject(ToastrService);
    public _offerStore: OfferStore = inject(OfferStore);
    private _achievementsStore: AchievementsStore = inject(AchievementsStore);
    private _abTestsStore: AbTestsStore = inject(AbTestsStore);

    public gameCategoriesStore: GameCategoriesStore = inject(GameCategoriesStore);
    public targetGroupsStore: TargetGroupsStore = inject(TargetGroupsStore);
    public offerGenresStore: OfferGenresStore = inject(OfferGenresStore);

    public achievements: WritableSignal<AchievementBackofficeViewModel[]> = signal<
        AchievementBackofficeViewModel[]
    >([]);

    public abTests: WritableSignal<AbTestViewModel[]> = signal<AbTestViewModel[]>([]);
    public subGenres: Signal<OfferGenreViewModel[]>;

    public generalForm: FormGroup<OfferGeneralForm> = new FormGroup<OfferGeneralForm>({
        title: new FormControl<Nullable<string>>(null),
        shortDescription: new FormControl<Nullable<string>>(null, [Validators.maxLength(30)]),
        description: new FormControl<Nullable<string>>(null),
        goalType: new FormControl<CampaignOfferGoalType[]>([CampaignOfferGoalType.None], {
            nonNullable: true,
        }),
        logoFile: new FormControl<Nullable<File>>(null),
        editorChoiceBannerFile: new FormControl<Nullable<File>>(null),
        smallBannerFile: new FormControl<Nullable<File>>(null),
    });

    public appInfoForm: FormGroup<OfferAppInfoForm> = new FormGroup<OfferAppInfoForm>({
        sourceType: new FormControl<OfferSourceType>(
            { value: OfferSourceType.Internal, disabled: true },
            {
                nonNullable: true,
            },
        ),
        appName: new FormControl<Nullable<string>>(null),
        affiseId: new FormControl<number>(0, { nonNullable: true }),
        mmpProvider: new FormControl<Nullable<MMPProviderType>>(null),
        appId: new FormControl<Nullable<string>>(null),
        bundleId: new FormControl<Nullable<string>>(null),
        securePostbackCode: new FormControl<Nullable<string>>(null),
        advertiserTrackingUrl: new FormControl<Nullable<string>>(null),
        userTrackingUrl: new FormControl<Nullable<string>>(null, {
            validators: [Validators.required],
        }),
    });

    public billingForm: FormGroup<OfferBillingForm> = new FormGroup<OfferBillingForm>({
        price: new FormControl<Nullable<number>>(null),
        billingType: new FormControl<BillingType>(BillingType.None, { nonNullable: true }),
        installsDailyCap: new FormControl<Nullable<number>>(null),
    });

    public targetingForm: FormGroup<OfferTargetingForm> = new FormGroup<OfferTargetingForm>({
        offerType: new FormControl<OfferType[]>([OfferType.None], { nonNullable: true }),
        offerPlacement: new FormControl<Nullable<OfferPlacement>>(null),
        platform: new FormControl<Platform>(Platform.Android, { nonNullable: true }),
        targetGroup: new FormControl<Nullable<number>>(null),
        gameCategories: new FormControl<Nullable<number[]>>(null),
        countryCodes: new FormControl<Nullable<string[]>>(null),
        order: new FormControl<number>(0, { nonNullable: true }),
        isAdminTop: new FormControl<boolean>(false, { nonNullable: true }),
        isFeatured: new FormControl<boolean>(false, { nonNullable: true }),
        orderInPlacement: new FormControl<number>(0, { nonNullable: true }),
    });

    public durationForm: FormGroup<OfferDurationForm> = new FormGroup<OfferDurationForm>({
        startDate: new FormControl<Nullable<DateTime>>(null),
        startDateHours: new FormControl<Nullable<number>>(null, {
            validators: [Validators.min(0), Validators.max(24)],
        }),
        startDateMinutes: new FormControl<Nullable<number>>(null, {
            validators: [Validators.min(0), Validators.max(60)],
        }),
        endDate: new FormControl<Nullable<DateTime>>(null),
        endDateHours: new FormControl<Nullable<number>>(null, {
            validators: [Validators.min(0), Validators.max(24)],
        }),
        endDateMinutes: new FormControl<Nullable<number>>(null, {
            validators: [Validators.min(0), Validators.max(60)],
        }),
        duration: new FormControl<Nullable<string>>(null),
    });

    public achievementForm: FormGroup<OfferAchievementForm> = new FormGroup<OfferAchievementForm>({
        achievementId: new FormControl<Nullable<number>>(null),
    });

    public abTestsForm: FormGroup<OfferAbTestsForm> = new FormGroup<OfferAbTestsForm>({
        forUsersWithoutAbGroup: new FormControl<boolean>(false, { nonNullable: true }),
        abTestGroupIds: new FormControl<Nullable<number[]>>([]),
    });

    public analyticsForm: FormGroup<OfferAnalyticsForm> = new FormGroup<OfferAnalyticsForm>({
        progressionType: new FormControl<Nullable<OfferProgressionType>>(null),
        genres: new FormControl<Nullable<string[]>>(null),
        subGenres: new FormControl<Nullable<string[]>>(null),
    });

    public miscForm: FormGroup<OfferMiscForm> = new FormGroup<OfferMiscForm>({
        isActive: new FormControl<boolean>(false, { nonNullable: true }),
    });

    public CampaignOfferGoalType: typeof CampaignOfferGoalType = CampaignOfferGoalType;
    public BillingType: typeof BillingType = BillingType;
    public MMPProviderType: typeof MMPProviderType = MMPProviderType;
    public OfferType: typeof OfferType = OfferType;
    public Platform: typeof Platform = Platform;
    public OfferPlacement: typeof OfferPlacement = OfferPlacement;
    public OfferProgressionType: typeof OfferProgressionType = OfferProgressionType;
    public OfferSourceType: typeof OfferSourceType = OfferSourceType;

    constructor() {
        effect(() => this.offerChangeEffectFn(), { allowSignalWrites: true });
        effect(() => this.achievementsChangeEffectFn(), { allowSignalWrites: true });
        effect(() => this.abTestsChangeEffectFn(), { allowSignalWrites: true });

        this.subGenres = toSignal(
            this.analyticsForm.controls.genres.valueChanges.pipe(
                map((genres) => {
                    const result = [] as OfferGenreViewModel[];

                    for (const genre of genres ?? []) {
                        const subGenres = this.offerGenresStore
                            .entities()
                            .find((x) => x.name === genre)?.subGenres;

                        if (subGenres) {
                            result.push(...subGenres);
                        }
                    }

                    return result;
                }),
            ),
            { initialValue: [] as OfferGenreViewModel[] },
        );

        this.appInfoForm.controls.affiseId.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((value) => {
                if (this.appInfoForm.getRawValue().sourceType !== OfferSourceType.Internal) {
                    this.appInfoForm.patchValue({
                        userTrackingUrl: environment.userTrackingUrl.replace(
                            "{offerId}",
                            value?.toString() ?? "",
                        ),
                    });
                }
            });
    }

    public urls = signalState({
        logoFile: undefined as Nullable<string>,
        editorChoiceBannerFile: undefined as Nullable<string>,
        smallBannerFile: undefined as Nullable<string>,
    });

    public onFileUploaded(event: Event, formControlName: string): void {
        const fileInput: HTMLInputElement | null = event.target as HTMLInputElement;

        if (fileInput && fileInput.files) {
            const file: File = fileInput.files[0];

            this.generalForm.get(formControlName)?.setValue(file, { emitModelToViewChange: false });

            patchState(this.urls, (previousValue) => ({
                ...previousValue,
                [formControlName]: URL.createObjectURL(file),
            }));
        }
    }

    public removeChip(tag: string, control: FormControl): void {
        if (control.value) {
            const index = control.value.indexOf(tag);

            if (index >= 0) {
                control.patchValue([
                    ...control.value.slice(0, index),
                    ...control.value.slice(index + 1),
                ]);
            }
        }
    }

    public addChip(event: MatChipInputEvent, control: FormControl): void {
        const value: string = event.value.trim();

        if (control.value) {
            control.patchValue([...control.value, value]);
        } else {
            control.patchValue([value]);
        }

        event.chipInput.clear();
    }

    public updateOffer() {
        const generalData = this.generalForm.getRawValue();
        const appData = this.appInfoForm.getRawValue();
        const billingData = this.billingForm.getRawValue();
        const targetingData = this.targetingForm.getRawValue();
        const durationData = this.durationForm.getRawValue();
        const achievementData = this.achievementForm.getRawValue();
        const abTestsData = this.abTestsForm.getRawValue();
        const analyticsData = this.analyticsForm.getRawValue();
        const miscData = this.miscForm.getRawValue();

        const updateModel: UpdateOfferEditModel = {
            id: this._offerStore.offerId(),
            affiseId: appData.affiseId,
            sourceType: appData.sourceType,
            securePostbackCode: appData.securePostbackCode,
            advertiserTrackingUrl: appData.advertiserTrackingUrl,
            mMPProvider: appData.mmpProvider,
            appName: appData.appName,
            appId: appData.appId,
            bundleId: appData.bundleId,
            title: generalData.title,
            shortDescription: generalData.shortDescription,
            description: generalData.description,
            userTrackingUrl: appData.userTrackingUrl,
            logoFile: generalData.logoFile,
            editorChoiceBannerFile: generalData.editorChoiceBannerFile,
            smallBannerFile: generalData.smallBannerFile,
            price: billingData.price,
            billingType: billingData.billingType,
            gameCategoryIds: targetingData.gameCategories,
            platform: targetingData.platform,
            offerType: targetingData.offerType.reduce((sum, value) => sum + value, OfferType.None),
            offerPlacement: targetingData.offerPlacement,
            targetGroupId: targetingData.targetGroup,
            isAdminTop: targetingData.isAdminTop,
            isFeatured: targetingData.isFeatured,
            countryCodes: targetingData.countryCodes,
            offerEndDate:
                durationData.endDate
                    ?.set({
                        hour: durationData.endDateHours ?? 0,
                        minute: durationData.endDateMinutes ?? 0,
                    })
                    .toISO() ?? null,
            offerDuration: durationData.duration,
            goalType: generalData.goalType.reduce((sum, value) => sum + value),
            isActive: miscData.isActive,
            order: targetingData.order,
            installsDailyCap: billingData.installsDailyCap,
            achievementId: achievementData.achievementId,
            forUsersWithoutAbGroup: abTestsData.forUsersWithoutAbGroup,
            abTestGroupIds: abTestsData.abTestGroupIds,
            progressionType: analyticsData.progressionType,
            genres: analyticsData.genres,
            subGenres: analyticsData.subGenres,
            offerStartDate:
                durationData.startDate
                    ?.set({
                        hour: durationData.startDateHours ?? 0,
                        minute: durationData.startDateMinutes ?? 0,
                    })
                    .toISO() ?? null,
            orderInPlacement: targetingData.orderInPlacement,
        };

        this._offerStore.updateOffer(updateModel).subscribe({
            next: () => {
                this._toastrService.success("Successfully updated");
            },
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }

    public hasInvalidForm(): boolean {
        return (
            this.generalForm.invalid ||
            this.appInfoForm.invalid ||
            this.billingForm.invalid ||
            this.targetingForm.invalid ||
            this.durationForm.invalid ||
            this.miscForm.invalid
        );
    }

    private offerChangeEffectFn() {
        const offer: BackOfficeOfferViewModel | null = this._offerStore.offer();

        if (offer) {
            patchState(this.urls, {
                logoFile: offer?.logoUrl,
                editorChoiceBannerFile: offer?.editorChoiceBannerUrl,
                smallBannerFile: offer?.smallBannerUrl,
            });

            this.generalForm.patchValue({
                title: offer?.title,
                shortDescription: offer.shortDescription,
                description: offer?.description,
                goalType: EnumUtils.convertFlagEnumToArray(offer?.goalType, CampaignOfferGoalType),
            });

            this.appInfoForm.patchValue({
                sourceType: offer?.sourceType,
                securePostbackCode: offer?.securePostbackCode,
                advertiserTrackingUrl: offer.advertiserTrackingUrl,
                appName: offer?.appName,
                affiseId: offer?.affiseOfferId,
                mmpProvider: offer?.mmpProvider,
                appId: offer?.appId,
                bundleId: offer?.bundleId,
            });

            if (offer.sourceType === OfferSourceType.Affise) {
                this.appInfoForm.controls.securePostbackCode.disable();
                this.appInfoForm.controls.advertiserTrackingUrl.disable();
                this.appInfoForm.controls.userTrackingUrl.disable();
            } else {
                this.appInfoForm.patchValue({ userTrackingUrl: offer.userTrackingUrl });
            }

            this.billingForm.patchValue({
                price: offer?.price,
                billingType: offer?.billingType,
                installsDailyCap: offer?.installsDailyCap,
            });

            this.targetingForm.patchValue({
                offerType: EnumUtils.convertFlagEnumToArray(offer?.offerType, OfferType),
                platform: offer?.platform,
                offerPlacement: offer?.offerPlacement,
                targetGroup: offer?.targetGroupId,
                gameCategories: offer?.gameCategoryIds,
                isAdminTop: offer?.isAdminTop,
                isFeatured: offer?.isFeatured,
                countryCodes: offer?.countryCodes,
                order: offer?.order,
                orderInPlacement: offer?.orderInPlacement,
            });

            if (offer?.offerEndDate) {
                const endDate: DateTime = DateTime.fromISO(offer.offerEndDate, { zone: "UTC" });

                this.durationForm.patchValue({
                    endDate: endDate.startOf("day"),
                    endDateHours: endDate.hour,
                    endDateMinutes: endDate.minute,
                });
            }

            if (offer?.offerStartDate) {
                const startDate: DateTime = DateTime.fromISO(offer.offerStartDate, { zone: "UTC" });

                this.durationForm.patchValue({
                    startDate: startDate.startOf("day"),
                    startDateHours: startDate.hour,
                    startDateMinutes: startDate.minute,
                });
            }

            this.durationForm.patchValue({
                duration: offer?.offerDuration,
            });

            this.abTestsForm.patchValue({
                forUsersWithoutAbGroup: offer?.forUsersWithoutAbGroup,
                abTestGroupIds: offer?.abTestGroupIds,
            });

            this.analyticsForm.patchValue({
                progressionType: offer?.progressionType,
                genres: offer?.genres,
                subGenres: offer?.subGenres,
            });

            this.miscForm.patchValue({
                isActive: offer?.isActive,
            });
        }
    }

    private achievementsChangeEffectFn() {
        this.achievements.set(
            this._achievementsStore
                .entities()
                .filter(
                    (x) =>
                        x.achievementType === AchievementType.MakeInAppPurchases ||
                        x.achievementType === AchievementType.CompleteGoals,
                ),
        );
    }

    private abTestsChangeEffectFn() {
        this.abTests.set(this._abTestsStore.entities());
    }
}
